/**
 * Pure types + defaults for the prescription form.
 *
 * Lives in its own module so unit tests (and any other consumer that
 * only wants the shape / canonical defaults) can import them without
 * dragging in PrescriptionForm.tsx's React tree + eager
 * `@/lib/supabase` import, which throws at test time when
 * VITE_SUPABASE_URL is unset.
 *
 * PrescriptionForm.tsx re-exports these for back-compat so the seven
 * existing call sites keep working.
 */
import type { PrescriptionDeepState } from './prescription/prescriptionState';
import { EVA_THICKNESS_DEFAULT_MM } from './prescription/evaThicknessRules';

/**
 * Prescription form state - captured per-line for custom orders.
 * Compatible with PrescriptionSuggestion from `packages/ai` so Phase 4f's
 * AI assistant can populate fields without a translation layer.
 */
export interface PrescriptionFormState {
  /** Discriminator. Drives which sub-form fields are relevant. */
  line_type:
    | 'custom_insole'
    | 'premium_3d_insole'
    | 'orthopedic_sandal'
    | 'sandal_insole_combo'
    | 'other_equipment';
  product_id?: string | undefined;
  unit_price_aed?: number | undefined;
  // Material / cover (custom_insole + premium)
  material?: string | undefined;
  thickness_mm?: number | undefined;
  cover?: string | undefined;
  // Sizes
  size_left_length_mm?: number | undefined;
  size_right_length_mm?: number | undefined;
  size_left_width_mm?: number | undefined;
  size_right_width_mm?: number | undefined;
  pathology?: string | undefined;
  observations?: string | undefined;
  // Sandal-specific
  upper_style?: string | undefined;
  base_color?: string | undefined;
  upper_color?: string | undefined;
  upper_straps_size?: string | undefined;
  size_eu?: number | undefined;
  // Modifications - structured per-region
  modifications: Array<PrescriptionModification>;
  // R28 - deep prescription state (Round 1+ ports the legacy app's detailed
  // form). Optional so existing drafts keep loading; sections gate on the
  // sub-objects being present.
  deep?: PrescriptionDeepState;
}

export interface PrescriptionModification {
  region: 'rearfoot' | 'midfoot' | 'forefoot' | 'offloading';
  modification_type: string;
  intensity?: 'light' | 'medium' | 'strong' | undefined;
  parameters?: Record<string, unknown> | undefined;
}

/**
 * R34 - Top cover defaults are seeded directly into the initial state
 * (Adult / EVA 2mm / DryFeet / Black) instead of relying on the
 * InsoleDesignSection's mount-init useEffect. The section is rendered
 * inside a CollapsibleCard that doesn't mount its children until the
 * user expands it, so the effect-based seed never fired on a fresh
 * order unless the user actively opened the design section. Persisting
 * the defaults at state-init means the prescription assistant sidebar,
 * draft save, and downstream summary all see them immediately.
 */
export const DEFAULT_PRESCRIPTION_STATE: PrescriptionFormState = {
  line_type: 'custom_insole',
  modifications: [],
  deep: {
    design: {
      top_cover_patient_type: 'adult',
      top_cover_material: 'EVA 2mm',
      top_cover_layer: 'DryFeet',
      top_cover_color: 'Black',
      insole_cup: 'medium_12',
      insole_length: 'full',
      // EVA base thickness, NEW prescriptions only. Seeded here so a fresh EVA
      // line carries 2 mm without the operator opening the design card. It is
      // meaningless on every other material, so buildLineConfiguration drops
      // it unless the line is EVA (designForMaterial.ts). No mount effect or
      // hydration path seeds it onto an EXISTING line, and the one route that
      // merges this default under a COPIED design (NewOrderPage's
      // onReorderPrefill, reached by the reorder-by-name prefill and by "Use
      // this prescription") strips it again unless the copied design carries
      // its own (mergeDesignForPrefill.ts): an old EVA line stays "Not
      // specified" until somebody picks a value.
      eva_thickness_mm: EVA_THICKNESS_DEFAULT_MM,
    },
    // Same reasoning as the top-cover defaults above, for the same reason, and
    // it now matters twice over.
    //
    // RearfootSection seeds these on FIRST MOUNT only (an `initialized` ref),
    // and CollapsibleCard does not render its children while collapsed, so the
    // effect fires only if the operator actually opens the Rearfoot card. Worse,
    // any code path that REBUILDS prescription state from this constant while
    // the card is open (the reorder prefill, and "Skip, start fresh") wipes the
    // node without the effect re-firing, because the component never unmounted.
    //
    // The manufacturing consequence is not cosmetic: buildRearfootSection in
    // prescriptionExcelExport.ts starts with `if (!rf) return null`, so an
    // absent rearfoot node drops the ENTIRE rearfoot section from the lab sheet,
    // including the "Rearfoot posting" row that is documented as always printed.
    // Seeding at state-init makes the node structurally present no matter which
    // cards were opened or which reset ran.
    //
    // Values mirror RearfootSection's DEFAULT_SUBCAL_SIDE and its posting seed
    // exactly, so this changes no behaviour for an operator who does open the
    // card. Verified against production: all 104 app-era custom lines and all
    // 19 sandal lines already carry deep.rearfoot, so this matches what the
    // form produces today rather than introducing anything new.
    rearfoot: {
      subcalcaneal_offloading: {
        left: { type: 'heel_offloading', fill: 'carbosan_3mm', depth_mm: -3 },
        right: { type: 'heel_offloading', fill: 'carbosan_3mm', depth_mm: -3 },
      },
      rearfoot_posting: { left_enabled: true, right_enabled: true },
    },
  },
};

/**
 * Has the operator actually put something in the prescription, as opposed to
 * just the defaults above?
 *
 * Used to decide whether resetting the prescription would DISCARD work and must
 * therefore ask first. It deliberately errs toward "yes": the structural
 * comparison below is order-sensitive, so a re-serialised-but-equivalent state
 * reads as content and the operator gets an extra confirmation. Prompting once
 * too often is a much cheaper mistake than silently wiping a filled-in
 * prescription, which is exactly the failure this guards.
 */
export function prescriptionHasOperatorContent(s: PrescriptionFormState): boolean {
  if (s.modifications && s.modifications.length > 0) return true;
  if ((s.observations ?? '').trim().length > 0) return true;
  if (s.material) return true;
  return (
    JSON.stringify(s.deep ?? {}) !== JSON.stringify(DEFAULT_PRESCRIPTION_STATE.deep ?? {})
  );
}
